import i18n from 'i18next';
import qs from 'qs';
import LocalStorage from 'utils/bom-dom-manipulation/local-storage';
import { clearCasinoPopupQuery } from 'utils/casino/casino';
import { CUSTOM_CATEGORIES } from 'utils/constants/casino/custom-categories';
import SpringConfigs, {
  androidWrapperApp,
  iOSWrapperApp
} from 'utils/constants/swarm/spring-configs';
import { getMountPath, mountPoint } from 'utils/generic/route-manipulation';
import { showToastError } from 'utils/generic/show-toast-error';
import { storageKeyName } from 'utils/generic/storage-key-name';
import { isMobile } from 'utils/is-mobile';
import { getDomain } from './get-domain';
import { GLOBALS } from './global-variables';
import { getSavedLocation } from 'components/QueryRoute';
import { ReservedRoutes } from '../constants/app/app-routes';
import { CASINO_LANG_EXCEPTIONS } from '../constants/languages/language-format';

export const deviceIdApp = {
  ANDROID_WRAPPER_APP: 4,
  IOS_WRAPPER_APP: 3
};

type GameURLParams = {
  frameId?: string;
  partnerId: number;
  devicetypeid: number;
  gameId: number | string;
  language: string;
  openType: 'fun' | 'real';
  isMobile: boolean;
  exitUrl?: string;
  token?: string;
  deposit_url?: string;
  browserUrl?: string;
  gameOptions?: null | string;
  logoSource?: string;
  BPUrl?: string;
};

const LAUNCH_TYPE =
  SpringConfigs.CASINO_LAUNCH_TYPE === 0
    ? 'Launch'
    : 'Inner/authorization.php/';

const LOUNCH_GAME_URL = `${LAUNCH_TYPE}${
  SpringConfigs.SHOW_GAMBLING_WITH_LOGO && isMobile() ? 'Uk' : ''
}${SpringConfigs.CASINO_LAUNCH_TYPE === 0 ? 'Game' : ''}`;

type TGameUrlProps = {
  gameId: string;
  gameType?: 'real' | 'fun';
  gameOptions?: null | string;
  scroll?: boolean;
  extraParams?: string;
  language?: string;
  exitUrlParams?: {
    includePreviousRoute?: boolean;
    exitUrlAsHomepage?: boolean;
    selectedCategory?: string;
    path?: string;
  };
  frameId?: string;
};

export const gameUrl = ({
  gameId,
  gameType = 'fun',
  gameOptions = null,
  scroll,
  extraParams,
  language,
  exitUrlParams,
  frameId
}: TGameUrlProps): string => {
  const lang = document.documentElement.lang;
  const mainDomain = getDomain();

  const savedLocation = getSavedLocation('casinoSingleGame', false, false);

  const queryParams = clearCasinoPopupQuery();

  const [beforeLocationQuery, afterLocationQuery] = savedLocation.split('?');
  let previousUrl = '';

  if (exitUrlParams) {
    previousUrl = `${
      beforeLocationQuery ||
      `${
        isMobile()
          ? getMountPath(exitUrlParams.path || '')
          : mountPoint(exitUrlParams.path || '', false, true)
      }/${exitUrlParams.selectedCategory || CUSTOM_CATEGORIES.all.id}`
    }?${
      !afterLocationQuery && queryParams?.size
        ? queryParams.toString()
        : afterLocationQuery || ''
    }`;
  }

  const exitUrl = exitUrlParams?.includePreviousRoute
    ? `${window.location.origin}${previousUrl}`
    : exitUrlParams?.exitUrlAsHomepage
    ? window.location.origin
    : window.location.href;

  const [urlPathnamePath, urlQueryPart] = exitUrl.split('?');

  let filteredExitUrl = exitUrl;

  if (urlQueryPart) {
    filteredExitUrl = `${urlPathnamePath}${qs.stringify(
      {
        ...qs.parse(urlQueryPart, {
          ignoreQueryPrefix: true
        }),
        ...{
          accounts: undefined,
          login: undefined
        }
      },
      { addQueryPrefix: true }
    )}`;
  }

  const mainUrl = `https://${
    SpringConfigs.CASINO_GAME_URL_PREFIX
  }.${mainDomain}/${!extraParams ? LOUNCH_GAME_URL : ''}?`;

  let deviceId = 1;

  if (androidWrapperApp) {
    deviceId = deviceIdApp.ANDROID_WRAPPER_APP;
  } else if (iOSWrapperApp) {
    deviceId = deviceIdApp.IOS_WRAPPER_APP;
  } else if (isMobile()) {
    deviceId = 2;
  }

  const siteLogoUrl = SpringConfigs.SITE_LOGO_URL.includes('https:/')
    ? SpringConfigs.SITE_LOGO_URL
    : window.location.origin + SpringConfigs.SITE_LOGO_URL;

  const bpUrl = `${window.location.origin}?accounts=%2A&nftRewards=%2A`;

  const params: GameURLParams = {
    partnerId:
      SpringConfigs.DECENTRALIZED_CASINO_PARTNER_ID || SpringConfigs.PARTNER_ID,
    devicetypeid: deviceId,
    gameId: gameId,
    language:
      language ||
      GLOBALS.languageConfigs.casinoLangPrefix ||
      CASINO_LANG_EXCEPTIONS[lang] ||
      CASINO_LANG_EXCEPTIONS[lang.slice(0, 2)] ||
      SpringConfigs.CURRENT_LANGUAGE.slice(0, 2),
    openType: gameType,
    isMobile: isMobile(),
    deposit_url:
      SpringConfigs.CURRENT_LANGUAGE_PREFIX ===
      SpringConfigs.DEFAULT_LANGUAGE_PREFIX
        ? `${window.location.origin}${ReservedRoutes.deposit}`
        : `${window.location.origin}/${SpringConfigs.CURRENT_LANGUAGE_PREFIX}${ReservedRoutes.deposit}`,
    browserUrl: window.location.href,
    logoSource: siteLogoUrl || undefined,
    exitUrl: filteredExitUrl,
    BPUrl: bpUrl
  };

  if (frameId) {
    params.frameId = frameId;
  }

  if (
    SpringConfigs.CASINO_MOBILE_IFRAME &&
    isMobile() &&
    document.body.style.overflow !== 'hidden' &&
    !scroll
  ) {
    document.body.classList.add('no-scroll');
  }

  if (gameType === 'real') {
    const token = JSON.parse(
      LocalStorage.getItem(storageKeyName('account', 'AUTH_DATA'))
    )?.auth_token;

    if (token) {
      params.token = token;
    } else {
      isMobile() && showToastError(i18n.t('casino.notLoggedInForPlayingReal'));
    }
  }

  let returnedUrl = `${mainUrl}${qs.stringify(params)}`;

  if (gameOptions) {
    returnedUrl += `&${gameOptions.split('"').join('')}`;
  }

  if (extraParams) {
    returnedUrl += extraParams;
  }

  if (
    SpringConfigs.SHOW_GAMBLING_WITH_LOGO &&
    !SpringConfigs.CASINO_MOBILE_IFRAME
  ) {
    returnedUrl += `&gambcomURL=${SpringConfigs.CASINO_GAMBLING_LOGO_URL}`;
  }

  return returnedUrl;
};
